import { useState, useEffect } from 'react';
import axios from 'axios';
import './Settings.css';

interface Config {
  convictionThreshold: number;
  initialCapital: number;
  liveTrading: boolean;
  maxPositionSize: number;
  maxConcurrentPositions: number;
  stopLossPercent: number;
  dailyLossLimit: number;
  scanIntervalMinutes: number;
  maxMarketsPerScan: number;
  glmMaxMarketsPerScan: number;
  glmUsagePauseThreshold: number;
  aiModel: string;
  takeProfitPercent: number;
  maxEntryPrice: number;
  skipCacheTtlHours: number;
  blockedCategories: string[];
  twoPassEnabled: boolean;
  newsCheckEnabled: boolean;
  convictionDecayHours: number;
  maxDailyTokenSpend: number;
  lookaheadHours: number;
}

export default function Settings() {
  const [config, setConfig] = useState<Config | null>(null);
  const [models, setModels] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [res, modelsRes] = await Promise.all([axios.get('/api/config'), axios.get('/api/models')]);
        setConfig(res.data);
        setModels(Array.isArray(modelsRes.data) ? modelsRes.data : modelsRes.data.models || []);
      } catch (err) {
        console.error('Failed to fetch config:', err);
      }
    };

    fetchConfig();
  }, []);

  const handleChange = (key: keyof Config, value: any) => {
    if (config) {
      setConfig({ ...config, [key]: value });
    }
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await axios.post('/api/config', config);
      setMessage('✅ Settings saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ Failed to save settings');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (!config) {
    return <div className="loading">Loading settings...</div>;
  }

  return (
    <div className="settings">
      <div className="settings-card">
        <h2>Trading Strategy</h2>
        <div className="setting-group">
          <label>Conviction Threshold (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            value={config.convictionThreshold}
            onChange={(e) => handleChange('convictionThreshold', parseFloat(e.target.value))}
          />
          <small>Only take trades with this confidence level or higher (0-100%)</small>
        </div>

        <div className="setting-group">
          <label>Initial Capital ($)</label>
          <input
            type="number"
            min="1"
            value={config.initialCapital}
            onChange={(e) => handleChange('initialCapital', parseFloat(e.target.value))}
          />
          <small>Starting capital for simulation/live trading</small>
        </div>

        <div className="setting-group">
          <label>AI Model</label>
          <select 
            value={config.aiModel || ''} 
            onChange={(e) => handleChange('aiModel', e.target.value)}
          >
            {models.map((model) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
          <small>Model used for market evaluations and predictions</small>
        </div>
      </div>

      <div className="settings-card">
        <h2>Risk Management</h2>
        <div className="setting-group">
          <label>Max Position Size (% of capital)</label>
          <input
            type="number"
            min="1"
            max="50"
            value={config.maxPositionSize}
            onChange={(e) => handleChange('maxPositionSize', parseFloat(e.target.value))}
          />
          <small>Maximum amount to risk per trade</small>
        </div>

        <div className="setting-group">
          <label>Max Concurrent Positions</label>
          <input
            type="number"
            min="1"
            max="20"
            value={config.maxConcurrentPositions}
            onChange={(e) => handleChange('maxConcurrentPositions', parseInt(e.target.value))}
          />
          <small>Maximum number of open positions at once</small>
        </div>

        <div className="setting-group">
          <label>Stop Loss (%)</label>
          <input
            type="number"
            min="0.1"
            max="10"
            step="0.1"
            value={config.stopLossPercent}
            onChange={(e) => handleChange('stopLossPercent', parseFloat(e.target.value))}
          />
          <small>Exit position if loss exceeds this percentage</small>
        </div>

        <div className="setting-group">
          <label>Take Profit (%)</label>
          <input
            type="number"
            min="10"
            max="500"
            step="5"
            value={config.takeProfitPercent ?? 80}
            onChange={(e) => handleChange('takeProfitPercent', parseFloat(e.target.value))}
          />
          <small>Close position automatically when gain exceeds this percentage</small>
        </div>

        <div className="setting-group">
          <label>Max Entry Price (¢)</label>
          <input
            type="number"
            min="50"
            max="99"
            step="1"
            value={Math.round((config.maxEntryPrice ?? 0.9) * 100)}
            onChange={(e) => handleChange('maxEntryPrice', parseFloat(e.target.value) / 100)}
          />
          <small>Skip trades filling above this price — near-certainty bets risk the whole stake to win pennies</small>
        </div>

        <div className="setting-group">
          <label>SKIP Cache TTL (hours)</label>
          <input
            type="number"
            min="0.5"
            max="24"
            step="0.5"
            value={config.skipCacheTtlHours ?? 2}
            onChange={(e) => handleChange('skipCacheTtlHours', parseFloat(e.target.value))}
          />
          <small>How long to cache a SKIP decision before re-evaluating the market</small>
        </div>

        <div className="setting-group">
          <label>
            <input
              type="checkbox"
              checked={config.twoPassEnabled ?? true}
              onChange={(e) => handleChange('twoPassEnabled', e.target.checked)}
            />
            {' '}Crypto first-pass pattern matching (free, local DB)
          </label>
          <small>Runs price-pattern analysis on crypto markets before full eval. Non-crypto markets go directly to full evaluation.</small>
        </div>

        <div className="setting-group">
          <label>
            <input
              type="checkbox"
              checked={config.newsCheckEnabled ?? true}
              onChange={(e) => handleChange('newsCheckEnabled', e.target.checked)}
            />
            {' '}News-aware position monitoring (30min web search on open positions)
          </label>
          <small>Searches for breaking news every 30 min per open position and exits if urgency ≥ 7/10</small>
        </div>

        <div className="setting-group">
          <label>Conviction Decay (hours)</label>
          <input
            type="number"
            min="0"
            max="168"
            step="6"
            value={config.convictionDecayHours ?? 48}
            onChange={(e) => handleChange('convictionDecayHours', parseFloat(e.target.value))}
          />
          <small>Re-evaluate stale positions after this many hours if price hasn't moved. Set 0 to disable.</small>
        </div>

        <div className="setting-group">
          <label>Blocked Categories (comma-separated)</label>
          <input
            type="text"
            placeholder="e.g. crypto, sports, weather"
            value={(config.blockedCategories ?? []).join(', ')}
            onChange={(e) => handleChange('blockedCategories', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))}
          />
          <small>Gamma API category names to skip entirely (bot won't evaluate these markets)</small>
        </div>

        <div className="setting-group">
          <label>Daily Loss Limit ($)</label>
          <input
            type="number"
            min="1"
            value={config.dailyLossLimit}
            onChange={(e) => handleChange('dailyLossLimit', parseFloat(e.target.value))}
          />
          <small>Stop trading if daily loss exceeds this amount</small>
        </div>

        <div className="setting-group">
          <label>Scan Interval (minutes)</label>
          <input
            type="number"
            min="1"
            max="120"
            value={config.scanIntervalMinutes ?? 5}
            onChange={(e) => handleChange('scanIntervalMinutes', parseInt(e.target.value))}
          />
          <small>How often the bot scans for new markets</small>
        </div>

        <div className="setting-group">
          <label>Max Markets Per Scan</label>
          <input
            type="number"
            min="10"
            max="500"
            step="10"
            value={config.maxMarketsPerScan ?? 100}
            onChange={(e) => handleChange('maxMarketsPerScan', parseInt(e.target.value))}
          />
          <small>How many markets are evaluated per scan. Higher = more opportunities found but more tokens used. Default: 100</small>
        </div>

        <div className="setting-group">
          <label>Max Markets Per Scan (GLM)</label>
          <input
            type="number"
            min="1"
            max="100"
            step="1"
            value={config.glmMaxMarketsPerScan ?? 15}
            onChange={(e) => handleChange('glmMaxMarketsPerScan', parseInt(e.target.value))}
          />
          <small>Scan cap used only when a GLM model is selected. Keep small — the GLM coding plan limits requests per 5-hour window. Default: 15</small>
        </div>

        <div className="setting-group">
          <label>GLM Quota Pause Threshold (%)</label>
          <input
            type="number"
            min="1"
            max="100"
            step="1"
            value={config.glmUsagePauseThreshold ?? 90}
            onChange={(e) => handleChange('glmUsagePauseThreshold', parseInt(e.target.value))}
          />
          <small>When a GLM model is selected, the bot skips scans once the 5-hour quota window reaches this percent used. Default: 90</small>
        </div>
      </div>

      <div className="settings-card warning">
        <h2>⚠️ Live Trading</h2>
        <div className="setting-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={config.liveTrading}
              onChange={(e) => handleChange('liveTrading', e.target.checked)}
            />
            <span>Enable Live Trading</span>
          </label>
          <small>
            WARNING: When enabled, real money will be traded. Only enable after validating
            profitability in simulation mode.
          </small>
        </div>


        <div className="setting-group">
          <label>Max Daily Token Spend ($)</label>
          <input
            type="number"
            step="0.10"
            min="0.10"
            max="50"
            value={config.maxDailyTokenSpend}
            onChange={(e) => handleChange('maxDailyTokenSpend', parseFloat(e.target.value))}
          />
          <small>Bot pauses AI calls if spend exceeds this in 24h. Default $1.00.</small>
        </div>
      </div>

      {message && <div className="message">{message}</div>}

      <button className="save-button" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save Settings'}
      </button>
    </div>
  );
}
